import React, {useEffect, useMemo, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useToast} from './Toast';
import {pickFile, readFileAsText, downloadText} from '../lib/browserFiles';
import {parseWordListFile} from '../lib/wordlistImport';

import {
  myWordlistsSelector,
  marketplaceWordlistsDataSelector,
  selectedWordlistIdSelector,
  selectedWordlistSelector,
  isUserWordlistsLoadingSelector,
  isUserWordlistsCollectionLoadingSelector,
  isMarketplaceWordlistsLoadingSelector,
  isWordlistDetailLoadingSelector,
  wordlistsErrorSelector,
} from '../store/selectors/wordlists.selectors';
import {currentProjectSelector, projectDataSelector} from '../store/selectors/projects.selectors';
import {
  fetchUserWordlists,
  fetchUserWordlistsCollection,
  fetchMarketplaceWordlists,
  fetchWordlistById,
  createWordlist,
  updateWordlist,
  deleteWordlist,
  addWordlistToCollection,
  removeWordlistFromCollection,
} from '../store/slices/wordlistsThunks';
import {setSelectedWordlistId, clearWordlistDetail, clearError} from '../store/slices/wordlistsSlice';
import {setDefaultWordlistThunk} from '../store/slices/projectsThunks';

import WordListTable from './wordlists/WordListTable';
import WordListDetail from './wordlists/WordListDetail';
import WordListFormModal, {WordListSubmission} from './wordlists/WordListFormModal';
import SetDefaultWordlistModal from './wordlists/SetDefaultWordlistModal';
import WordListsToolbar, {NewWordListButton, WordListTab} from './wordlists/WordListsToolbar';
import {DeleteConfirmationModal} from './DeleteConfirmationModal';

/**
 * Word lists: the library on the left, the words themselves on the right.
 *
 * The desktop-era page put two large card grids behind a five-colour tab strip and hid the words —
 * the only thing a list *is* — in a panel below the fold. Here the list is a row, selecting it fills
 * a panel that stays in view while you scroll, and the marketplace is a second tab of the same
 * table rather than a differently-shaped screen.
 */
const WordListsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();

  const myLists = useAppSelector(myWordlistsSelector);
  const marketplaceWordlists = useAppSelector(marketplaceWordlistsDataSelector);
  const selectedListId = useAppSelector(selectedWordlistIdSelector);
  const selectedList = useAppSelector(selectedWordlistSelector);
  const isLoadingUser = useAppSelector(isUserWordlistsLoadingSelector);
  const isLoadingCollection = useAppSelector(isUserWordlistsCollectionLoadingSelector);
  const isLoadingMarketplace = useAppSelector(isMarketplaceWordlistsLoadingSelector);
  const isLoadingDetail = useAppSelector(isWordlistDetailLoadingSelector);
  const error = useAppSelector(wordlistsErrorSelector);
  const projects = useAppSelector(projectDataSelector);
  const currentProject = useAppSelector(currentProjectSelector);

  const [tab, setTab] = useState<WordListTab>('mine');
  const [search, setSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [showSetDefault, setShowSetDefault] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [listToDelete, setListToDelete] = useState<{id: string; name: string} | null>(null);

  const collectionIds = useMemo(() => new Set(myLists.map(list => list.id)), [myLists]);

  const marketplaceLists = useMemo(
    () => marketplaceWordlists.filter(list => list && !list.isDeleted && !collectionIds.has(list.id)),
    [marketplaceWordlists, collectionIds]
  );

  const isLoading = isLoadingUser || isLoadingCollection || isLoadingMarketplace;
  const visibleLists = tab === 'mine' ? myLists : marketplaceLists;

  const filteredLists = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? visibleLists.filter(list => list.name.toLowerCase().includes(query)) : visibleLists;
  }, [visibleLists, search]);

  useEffect(() => {
    dispatch(fetchUserWordlists());
    dispatch(fetchUserWordlistsCollection());
    dispatch(fetchMarketplaceWordlists());
  }, [dispatch]);

  // The panel is the point of the page, so it opens on something rather than an empty state.
  useEffect(() => {
    if (myLists.length > 0 && !selectedListId) {
      dispatch(setSelectedWordlistId(myLists[0].id));
    }
  }, [myLists, selectedListId, dispatch]);

  // The lists carry a word *count*; only the detail endpoint carries the words.
  useEffect(() => {
    if (selectedListId) {
      dispatch(clearWordlistDetail());
      dispatch(fetchWordlistById(selectedListId));
    }
  }, [selectedListId, dispatch]);

  // The detail is the only copy guaranteed to carry every word, so the form prefers it and falls
  // back to the row — editing a list off the detail's words is how you save a truncated list.
  const editingList = editingListId
    ? (selectedList?.id === editingListId ? selectedList : null)
      ?? myLists.find(list => list.id === editingListId)
      ?? null
    : null;

  const refreshMyLists = async () => {
    await dispatch(fetchUserWordlists()).unwrap();
    await dispatch(fetchUserWordlistsCollection()).unwrap();
  };

  const handleSubmitForm = async (submission: WordListSubmission) => {
    setIsSaving(true);
    try {
      if (formMode === 'edit' && editingList) {
        await dispatch(updateWordlist({
          id: editingList.id,
          name: submission.name,
          description: submission.description || undefined,
          isPublic: submission.isPublic,
          words: submission.words,
        })).unwrap();
        // The slice folds the response back into the lists and the detail, so nothing is refetched.
        showToast(`“${submission.name}” saved`, 'success');
      } else {
        const created = await dispatch(createWordlist({
          name: submission.name,
          words: submission.words,
          description: submission.description || undefined,
          isPublic: submission.isPublic,
        })).unwrap();
        // The backend adds a new list to the user's collection itself.
        await dispatch(fetchUserWordlistsCollection()).unwrap();
        dispatch(setSelectedWordlistId(created.id));
        setTab('mine');
        showToast(`“${submission.name}” created`, 'success');
      }
      setFormMode(null);
      setEditingListId(null);
    } catch {
      showToast('Could not save that word list', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePublic = async (listId: string, isPublic: boolean) => {
    setIsSaving(true);
    try {
      await dispatch(updateWordlist({id: listId, isPublic})).unwrap();
      showToast(isPublic ? 'List is now public' : 'List is now private', 'success');
    } catch {
      showToast('Could not change who can see that list', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!listToDelete) {
      return;
    }

    setIsSaving(true);
    try {
      await dispatch(deleteWordlist(listToDelete.id)).unwrap();
      if (selectedListId === listToDelete.id) {
        dispatch(setSelectedWordlistId(null));
      }
      await refreshMyLists();
      showToast(`“${listToDelete.name}” deleted`, 'success');
    } catch {
      showToast('Could not delete that word list', 'error');
    } finally {
      setIsSaving(false);
      setListToDelete(null);
    }
  };

  const handleAddToCollection = async (listId: string) => {
    setIsSaving(true);
    try {
      await dispatch(addWordlistToCollection(listId)).unwrap();
      await dispatch(fetchUserWordlistsCollection()).unwrap();
      showToast('Added to your lists', 'success');
    } catch {
      showToast('Could not add that list', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveFromCollection = async (listId: string) => {
    setIsSaving(true);
    try {
      await dispatch(removeWordlistFromCollection(listId)).unwrap();
      if (selectedListId === listId) {
        dispatch(setSelectedWordlistId(null));
      }
      showToast('Removed from your lists', 'success');
    } catch {
      showToast('Could not remove that list', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImport = async () => {
    const file = await pickFile({accept: '.csv,.txt'});

    if (!file) {
      return;
    }

    setIsImporting(true);
    try {
      const parsed = parseWordListFile(await readFileAsText(file), file.name || 'Imported list');

      if (parsed.length === 0) {
        showToast('No word lists were found in that file', 'warning');
        return;
      }

      for (const {name, words} of parsed) {
        // A name that already exists merges and dedupes rather than creating a second list.
        const existing = myLists.find(list => list.name === name);

        if (existing) {
          await dispatch(updateWordlist({
            id: existing.id,
            words: [...new Set([...(existing.words ?? []), ...words])],
          })).unwrap();
        } else {
          const created = await dispatch(createWordlist({name, words, isPublic: false})).unwrap();
          await dispatch(addWordlistToCollection(created.id)).unwrap();
        }
      }

      await dispatch(fetchUserWordlistsCollection()).unwrap();
      setTab('mine');
      showToast(
        parsed.length === 1
          ? `Imported “${parsed[0].name}” — ${parsed[0].words.length} words`
          : `Imported ${parsed.length} word lists`,
        'success'
      );
    } catch {
      showToast('Could not import that file', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleExportCsv = () => {
    if (!selectedList) {
      return;
    }
    downloadText(`${selectedList.words.join(',')}\n`, `${selectedList.name}.csv`, 'text/csv;charset=utf-8');
  };

  const handleSetDefault = async (projectId: string) => {
    if (!selectedList) {
      return;
    }

    setIsSettingDefault(true);
    try {
      await dispatch(setDefaultWordlistThunk({projectId, wordlistId: selectedList.id})).unwrap();
      const project = projects.find(candidate => candidate.id === projectId);
      showToast(`“${selectedList.name}” is the default for “${project?.name ?? 'project'}”`, 'success');
      setShowSetDefault(false);
    } catch {
      showToast('Could not set that default word list', 'error');
    } finally {
      setIsSettingDefault(false);
    }
  };

  const openCreateForm = () => {
    setEditingListId(null);
    setFormMode('create');
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-950">
      <div className="mx-auto max-w-6xl px-4 py-6 space-y-5">
        <WordListsToolbar
          tab={tab}
          onTabChange={(nextTab) => {
            setTab(nextTab);
            setSearch('');
          }}
          myCount={myLists.length}
          marketplaceCount={marketplaceLists.length}
          isImporting={isImporting}
          onImport={handleImport}
          onCreate={openCreateForm}
          error={error}
          onDismissError={() => dispatch(clearError())}
        />

        <div className="grid gap-5 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <WordListTable
              heading={tab === 'mine' ? 'My lists' : 'Marketplace'}
              lists={filteredLists}
              variant={tab}
              collectionIds={collectionIds}
              selectedId={selectedListId}
              isLoading={isLoading}
              isBusy={isSaving}
              search={search}
              onSearchChange={setSearch}
              empty={
                tab === 'mine'
                  ? {
                    title: 'No word lists yet',
                    body: 'Create one, import a .csv or .txt, or add a ready-made list from the marketplace.',
                    action: <NewWordListButton onClick={openCreateForm} />,
                  }
                  : {
                    title: 'Nothing left to add',
                    body: 'Every public list is already in your collection.',
                  }
              }
              onSelect={id => dispatch(setSelectedWordlistId(id))}
              onEdit={(id) => {
                setEditingListId(id);
                setFormMode('edit');
              }}
              onTogglePublic={handleTogglePublic}
              onDelete={(id) => {
                const list = myLists.find(candidate => candidate.id === id);
                setListToDelete({id, name: list?.name ?? id});
              }}
              onRemoveFromCollection={handleRemoveFromCollection}
              onAddToCollection={handleAddToCollection}
            />
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-6 lg:self-start">
            <WordListDetail
              list={selectedList}
              isLoading={isLoadingDetail}
              hasAnyLists={myLists.length > 0 || marketplaceLists.length > 0}
              isInCollection={selectedList ? collectionIds.has(selectedList.id) : false}
              isBusy={isSaving}
              onEdit={(id) => {
                setEditingListId(id);
                setFormMode('edit');
              }}
              onExportCsv={handleExportCsv}
              onSetDefault={() => setShowSetDefault(true)}
              onAddToCollection={handleAddToCollection}
              onRemoveFromCollection={handleRemoveFromCollection}
            />
          </div>
        </div>

        <p className="max-w-2xl px-1 text-xs text-gray-600">
          Import formats: a <span className="text-gray-500">.csv</span> holds one list per line as
          <span className="text-gray-500"> name,word,word</span>; a{' '}
          <span className="text-gray-500">.txt</span> becomes a list named after the file, one word
          per line. Importing over a list you already have merges the words instead of duplicating it.
        </p>
      </div>

      <WordListFormModal
        isOpen={formMode !== null}
        mode={formMode ?? 'create'}
        wordlist={formMode === 'edit' ? editingList : null}
        isSaving={isSaving}
        onClose={() => {
          setFormMode(null);
          setEditingListId(null);
        }}
        onSubmit={handleSubmitForm}
      />

      <SetDefaultWordlistModal
        isOpen={showSetDefault}
        onClose={() => setShowSetDefault(false)}
        onConfirm={handleSetDefault}
        isConfirming={isSettingDefault}
        wordlistName={selectedList?.name ?? ''}
        projects={projects}
        currentProjectId={currentProject?.id}
      />

      <DeleteConfirmationModal
        isOpen={listToDelete !== null}
        listName={listToDelete?.name ?? null}
        isDeleting={isSaving}
        onConfirm={handleConfirmDelete}
        onCancel={() => setListToDelete(null)}
      />
    </div>
  );
};

export default WordListsContainer;
